"use client";
import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Paper,
  Chip,
  InputBase,
  CircularProgress,
  Badge,
  Divider,
  Backdrop,
  Tabs,
  Tab,
  AvatarGroup,
  RadioGroup,
  FormControlLabel,
  Radio,
  Autocomplete,
  Tooltip,
} from "@mui/material";
import {
  Send as SendIcon,
  Search as SearchIcon,
  Add as AddIcon,
  MoreVert as MoreIcon,
  EmojiEmotionsOutlined as EmojiIcon,
  AttachFile as AttachIcon,
  GroupOutlined as GroupIcon,
  PersonOutline as PersonIcon,
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorIcon,
} from "@mui/icons-material";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  fetchConversations,
  fetchChatHistory,
  sendChatMessage,
  fetchGroupChatHistory,
  sendGroupChatMessage,
} from "../../api/services/TextMessage/textMessage";
import { QK } from "../../base/qk";
import { searchClients } from "../../api/services/Client/searchClient";
import { searchCandidates } from "../../api/services/candidate";
import GroupConfirmModal from "./ConfrimModal/GroupConfirmModal";

const TextMessage = ({ conversationId: initialConvId = null }) => {
  const queryClient = useQueryClient();
  const [tabValue, setTabValue] = useState(1);
  const [selectedChat, setSelectedChat] = useState(null);
  const [message, setMessage] = useState("");
  const [isComposeMode, setIsComposeMode] = useState(false);
  const [newRecipient, setNewRecipient] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const [sendTarget, setSendTarget] = useState("selected");
  const [selectedGroupEmployees, setSelectedGroupEmployees] = useState([]);
  const [candidateSearchTerm, setCandidateSearchTerm] = useState("");
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  // Pagination State - Set to 5 for testing
  const [limit, setLimit] = useState(10);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const scrollRef = useRef(null);

  const { data: searchResults = [], isFetching: isSearching } = useQuery(
    ["clientSearch", searchTerm],
    () => searchClients(searchTerm),
    {
      enabled: tabValue === 0 && searchTerm.length >= 2,
      keepPreviousData: true,
    }
  );

  const { data: candidateResults = [], isFetching: isSearchingCandidates } =
    useQuery(
      ["candidateSearch", candidateSearchTerm],
      () => searchCandidates(candidateSearchTerm),
      {
        enabled:
          tabValue === 1 &&
          sendTarget === "selected" &&
          candidateSearchTerm.length >= 2,
        keepPreviousData: true,
      }
    );

  const { data: conversations, isLoading: isListLoading } = useQuery(
    QK.conversations,
    fetchConversations,
    {
      refetchOnWindowFocus: false,
      refetchInterval: 30000,
    }
  );

  const activePhoneNumber = isComposeMode
    ? newRecipient
    : selectedChat?.chat_partner || initialConvId;

  // Updated Query with limit in Key
  const {
    data: historyRes,
    isLoading: isHistoryLoading,
    isFetching: isHistoryFetching,
  } = useQuery(
    [QK.chatHistory, activePhoneNumber, tabValue, limit],
    () => {
      if (tabValue === 1) return fetchGroupChatHistory(limit);
      return fetchChatHistory(activePhoneNumber);
    },
    {
      enabled: (!!activePhoneNumber || tabValue === 1) && !isComposeMode,
      refetchOnWindowFocus: false,
      refetchInterval: 30000,
      keepPreviousData: true, // Prevents loading overlay on every fetch
    }
  );

  const mutation = useMutation(
    (payload) =>
      payload.is_group
        ? sendGroupChatMessage(payload)
        : sendChatMessage(payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QK.conversations);
        queryClient.invalidateQueries([
          QK.chatHistory,
          activePhoneNumber,
          tabValue,
          limit,
        ]);
        setMessage("");
        setIsComposeMode(false);
        setSelectedGroupEmployees([]);
        setIsConfirmModalOpen(false);
        setIsInitialLoad(true);
      },
    }
  );

  // Auto Scroll to Bottom on initial load
  useEffect(() => {
    if (scrollRef.current && isInitialLoad && !isHistoryLoading) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      setIsInitialLoad(false);
    }
  }, [historyRes, isInitialLoad, isHistoryLoading]);

  // Handle Scroll to Load More (Pagination Logic)
  const handleScroll = (e) => {
    const { scrollTop, scrollHeight } = e.target;
    // When user scrolls to top and more messages exist
    if (scrollTop === 0 && historyRes?.has_more && !isHistoryFetching) {
      const prevHeight = scrollHeight;
      setLimit((prev) => prev + 10);

      // Keep the scroll position stable after prepending new items
      setTimeout(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop =
            scrollRef.current.scrollHeight - prevHeight;
        }
      }, 150);
    }
  };

  const filteredConversations = useMemo(() => {
    const list = conversations?.data || [];
    return tabValue === 0
      ? list.filter((c) => !c.is_group)
      : list.filter((c) => c.is_group);
  }, [conversations, tabValue]);

  const handleSelectClient = (client) => {
    const phone = client.phone_number;
    const existingChat = (conversations?.data || []).find(
      (c) => c.chat_partner === phone
    );

    if (existingChat) {
      setSelectedChat(existingChat);
      setIsComposeMode(false);
    } else {
      setNewRecipient(phone);
      setIsComposeMode(true);
      setSelectedChat(null);
    }
    setSearchTerm("");
    setLimit(10);
    setIsInitialLoad(true);
  };

  const onSendMessage = (e) => {
    if (e) e.preventDefault();
    if (!message.trim()) return;
    if (tabValue === 1) {
      setIsConfirmModalOpen(true);
      return;
    }
    handleFinalSend();
  };

  const handleFinalSend = () => {
    const isGroup = tabValue === 1;
    const targetPhone = isComposeMode ? newRecipient : activePhoneNumber;

    if (!isGroup && !targetPhone) return;

    const payload = {
      body: message,
      sender: "AllForCare",
      is_group: isGroup,
      client: isGroup ? null : 1,
      moderator: 1,
    };

    if (isGroup) {
      payload.sendTarget = sendTarget;
      payload.recipients =
        sendTarget === "all"
          ? "ALL_EMPLOYEES"
          : selectedGroupEmployees.map((emp) => emp.mobile_phone);
      payload.recipient_count =
        sendTarget === "all" ? "All" : selectedGroupEmployees.length;
    } else {
      payload.recipient = targetPhone.replace(/[^\d+]/g, "");
    }

    mutation.mutate(payload);
  };

  const showDropdown =
    tabValue === 0 && searchTerm.length >= 2 && searchResults.length > 0;

  return (
    <Box
      sx={{
        display: "flex",
        height: "90vh",
        m: 1,
        bgcolor: "#F8F9FB",
        borderRadius: "24px",
        overflow: "hidden",
        border: "1px solid rgba(224, 230, 237, 0.5)",
      }}
    >
      <Box
        sx={{
          width: 360,
          display: "flex",
          flexDirection: "column",
          bgcolor: "#fff",
          borderRight: "1px solid #F1F3F5",
        }}
      >
        <Box sx={{ p: 3, pb: 1, position: "relative" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 800, color: "#111827" }}>
              Messages
            </Typography>
            {tabValue === 0 && (
              <IconButton
                onClick={() => {
                  setIsComposeMode(true);
                  setSelectedChat(null);
                  setSearchTerm("");
                  setLimit(10);
                  setIsInitialLoad(true);
                }}
                sx={{ bgcolor: "#EBF5FF", color: "#007FFF" }}
              >
                <AddIcon sx={{ fontSize: 20 }} />
              </IconButton>
            )}
          </Box>
          <Tabs
            value={tabValue}
            onChange={(e, v) => {
              setTabValue(v);
              setIsComposeMode(false);
              setSelectedChat(null);
              setSearchTerm("");
              setLimit(10);
              setIsInitialLoad(true);
            }}
            sx={{
              mb: 2,
              minHeight: 40,
              "& .MuiTab-root": {
                minHeight: 40,
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.85rem",
              },
            }}
          >
            <Tab
              icon={<PersonIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label="Direct"
            />
            <Tab
              icon={<GroupIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label="Groups"
            />
          </Tabs>
          <TextField
            fullWidth
            size="small"
            placeholder={
              tabValue === 0 ? "Search clients..." : "Search groups..."
            }
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: isSearching ? (
                <CircularProgress size={16} sx={{ mr: 1 }} />
              ) : (
                <SearchIcon sx={{ color: "#9CA3AF", mr: 1, fontSize: 18 }} />
              ),
              sx: {
                borderRadius: "12px",
                bgcolor: "#F3F4F6",
                border: "none",
                "& fieldset": { border: "none" },
              },
            }}
          />
          {showDropdown && (
            <Paper
              elevation={8}
              sx={{
                position: "absolute",
                top: "100%",
                left: 24,
                right: 24,
                zIndex: 1100,
                borderRadius: "12px",
                mt: 1,
                maxHeight: 250,
                overflowY: "auto",
              }}
            >
              <List p={0}>
                {searchResults.map((client) => (
                  <ListItem
                    button
                    key={client.id}
                    onClick={() => handleSelectClient(client)}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ fontSize: 12 }}>
                        {client.full_name?.substring(0, 2)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={client.full_name}
                      secondary={client.phone_number}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          )}
        </Box>
        <List sx={{ flex: 1, overflowY: "auto", px: 1.5 }}>
          {isListLoading ? (
            <CircularProgress
              size={20}
              sx={{ display: "block", m: "20px auto" }}
            />
          ) : (
            filteredConversations.map((chat) => (
              <ListItem
                button
                key={chat.id}
                onClick={() => {
                  setSelectedChat(chat);
                  setIsComposeMode(false);
                  setLimit(10);
                  setIsInitialLoad(true);
                }}
                sx={{
                  borderRadius: "16px",
                  mb: 0.5,
                  bgcolor:
                    selectedChat?.id === chat.id ? "#EBF5FF" : "transparent",
                }}
              >
                <ListItemAvatar>
                  <Avatar
                    sx={{ bgcolor: chat.is_group ? "#818CF8" : "#007FFF" }}
                  >
                    {chat.is_group ? (
                      <GroupIcon fontSize="small" />
                    ) : (
                      chat.chat_partner?.substring(0, 2)
                    )}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Typography sx={{ fontWeight: 700, fontSize: "0.9rem" }}>
                      {chat.chat_partner}
                    </Typography>
                  }
                  secondary={
                    <Typography
                      variant="caption"
                      noWrap
                      sx={{ display: "block" }}
                    >
                      {chat.body}
                    </Typography>
                  }
                />
              </ListItem>
            ))
          )}
        </List>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          bgcolor: "#fff",
          position: "relative",
        }}
      >
        <Backdrop
          sx={{
            color: "#007FFF",
            zIndex: 10,
            position: "absolute",
            bgcolor: "rgba(255,255,255,0.6)",
          }}
          open={mutation.isLoading}
        >
          <CircularProgress color="inherit" size={40} />
        </Backdrop>
        <Box
          sx={{
            px: 4,
            py: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid #F3F4F6",
          }}
        >
          {tabValue === 1 ? (
            <Box
              sx={{ display: "flex", flexDirection: "column", width: "100%" }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Avatar sx={{ bgcolor: "#818CF8", mr: 2 }}>
                    <GroupIcon />
                  </Avatar>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                    Broadcast to Employees
                  </Typography>
                </Box>
                <RadioGroup
                  row
                  value={sendTarget}
                  onChange={(e) => setSendTarget(e.target.value)}
                >
                  <FormControlLabel
                    value="all"
                    control={<Radio size="small" />}
                    label={
                      <Typography sx={{ fontSize: "12px" }}>All</Typography>
                    }
                  />
                  <FormControlLabel
                    value="selected"
                    control={<Radio size="small" />}
                    label={
                      <Typography sx={{ fontSize: "12px" }}>
                        Selected
                      </Typography>
                    }
                  />
                </RadioGroup>
              </Box>
              {sendTarget === "selected" && (
                <Autocomplete
                  multiple
                  size="small"
                  options={candidateResults || []}
                  loading={isSearchingCandidates}
                  getOptionLabel={(option) => `${option.full_name}`}
                  filterOptions={(x) => x}
                  onInputChange={(event, newInputValue) =>
                    setCandidateSearchTerm(newInputValue)
                  }
                  onChange={(event, newValue) =>
                    setSelectedGroupEmployees(newValue)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      placeholder="Search candidates by name..."
                      sx={{ mt: 1 }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {isSearchingCandidates ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              )}
            </Box>
          ) : isComposeMode ? (
            <Box sx={{ flex: 1, display: "flex", alignItems: "center" }}>
              <Typography sx={{ fontWeight: 700, color: "#007FFF", mr: 2 }}>
                To:
              </Typography>
              <InputBase
                fullWidth
                placeholder="Recipient number..."
                value={newRecipient}
                onChange={(e) => setNewRecipient(e.target.value)}
              />
            </Box>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Avatar sx={{ bgcolor: "#007FFF", mr: 2 }}>
                {activePhoneNumber?.substring(0, 2)}
              </Avatar>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  {selectedChat?.chat_partner ||
                    activePhoneNumber ||
                    "Select Chat"}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "#10B981", fontWeight: 600 }}
                >
                  Direct Message
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        <Box
          ref={scrollRef}
          onScroll={tabValue === 1 ? handleScroll : null}
          sx={{ flex: 1, p: 4, overflowY: "auto" }}
        >
          {/* Progress loader for more data fetching */}
          {isHistoryFetching && limit > 10 && (
            <Box sx={{ textAlign: "center", py: 1 }}>
              <CircularProgress size={18} />
            </Box>
          )}

          {isHistoryLoading && limit === 10 ? (
            <CircularProgress size={24} sx={{ display: "block", m: "auto" }} />
          ) : (
            <>
              {historyRes?.has_more && (
                <Box sx={{ textAlign: "center", py: 1 }}>
                  <Typography variant="caption" sx={{ color: "#9CA3AF" }}>
                    Scroll up to load more
                  </Typography>
                </Box>
              )}
              {historyRes?.data?.map((msg, index) => {
                const isOutbound = msg.direction === "OUTBOUND";

                const recipients = msg.delivery_info?.recipients || [];

                const recipientNames = recipients
                  .map((r) => (typeof r === "object" ? r.name : null))
                  .filter(Boolean);

                const hasRecipientNames = recipientNames.length > 0;

                return (
                  <Box
                    key={msg.id || index}
                    sx={{
                      display: "flex",
                      justifyContent: isOutbound ? "flex-end" : "flex-start",
                      mb: 3,
                    }}
                  >
                    <Box sx={{ maxWidth: "75%" }}>
                      {!isOutbound && tabValue === 1 && (
                        <Typography
                          sx={{
                            fontSize: "10px",
                            fontWeight: 700,
                            ml: 1,
                            mb: 0.5,
                            color: "#6B7280",
                          }}
                        >
                          {msg.sender_name || "Employee"}
                        </Typography>
                      )}

                      <Paper
                        elevation={0}
                        sx={{
                          p: "12px 18px",
                          borderRadius: isOutbound
                            ? "20px 20px 4px 20px"
                            : "20px 20px 20px 4px",
                          bgcolor: isOutbound ? "#007FFF" : "#F3F4F6",
                          color: isOutbound ? "#fff" : "#1F2937",
                        }}
                      >
                        <Typography sx={{ fontSize: "0.9rem" }}>
                          {msg.body}
                        </Typography>
                      </Paper>

                      {isOutbound && tabValue === 1 && (
                        <Box
                          sx={{
                            mt: 0.5,
                            px: 1,
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                          }}
                        >
                          {msg.delivery_info && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Tooltip title="Success">
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    color: "#10B981",
                                  }}
                                >
                                  <CheckCircleIcon
                                    sx={{ fontSize: 12, mr: 0.3 }}
                                  />
                                  <Typography
                                    sx={{ fontSize: 10, fontWeight: 700 }}
                                  >
                                    {msg.delivery_info.success || 0}
                                  </Typography>
                                </Box>
                              </Tooltip>

                              <Tooltip title="Failed">
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    color: "#EF4444",
                                  }}
                                >
                                  <ErrorIcon sx={{ fontSize: 12, mr: 0.3 }} />
                                  <Typography
                                    sx={{ fontSize: 10, fontWeight: 700 }}
                                  >
                                    {msg.delivery_info.failed || 0}
                                  </Typography>
                                </Box>
                              </Tooltip>
                            </Box>
                          )}

                          {/* নামসমূহ - আইকনের ঠিক পাশে */}
                          {hasRecipientNames && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                ml: 1.5,
                              }}
                            >
                              <Tooltip title={recipientNames.join(", ")} arrow>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.8,
                                  }}
                                >
                                  {/* একটি সিম্পল সেপারেটর ডট */}
                                  <Typography
                                    sx={{ color: "#D1D5DB", fontSize: "12px" }}
                                  >
                                    |
                                  </Typography>

                                  <Typography
                                    noWrap
                                    sx={{
                                      fontSize: "11px",
                                      fontWeight: 600,
                                      color: "#4B5563", // Soft Dark Grey
                                      letterSpacing: "0.2px",
                                      maxWidth: "160px",
                                      cursor: "default",
                                      display: "flex",
                                      alignItems: "center",
                                      "&:hover": { color: "#007FFF" },
                                    }}
                                  >
                                    {recipientNames.length > 1
                                      ? `${recipientNames[0]} +${
                                          recipientNames.length - 1
                                        }`
                                      : recipientNames[0]}
                                  </Typography>
                                </Box>
                              </Tooltip>
                            </Box>
                          )}
                        </Box>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </>
          )}
        </Box>

        <Box sx={{ px: 4, py: 3 }}>
          <Paper
            elevation={0}
            sx={{
              p: "6px 12px",
              display: "flex",
              alignItems: "center",
              borderRadius: "16px",
              bgcolor: "#F3F4F6",
            }}
          >
            <IconButton size="small" sx={{ color: "#007FFF" }}>
              <AttachIcon fontSize="small" />
            </IconButton>
            <InputBase
              sx={{ flex: 1, mx: 1.5, fontSize: "0.9rem" }}
              placeholder={
                tabValue === 1
                  ? sendTarget === "all"
                    ? "Message all employees..."
                    : `Message ${selectedGroupEmployees.length} selected...`
                  : "Start typing..."
              }
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  onSendMessage();
                }
              }}
            />
            <IconButton
              onClick={onSendMessage}
              disabled={
                !message.trim() ||
                (tabValue === 1 &&
                  sendTarget === "selected" &&
                  selectedGroupEmployees.length === 0)
              }
              sx={{
                bgcolor: "#007FFF",
                color: "#fff",
                "&:hover": { bgcolor: "#0066CC" },
              }}
            >
              <SendIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Paper>
        </Box>
      </Box>

      <GroupConfirmModal
        open={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={handleFinalSend}
        targetType={sendTarget}
        targetCount={selectedGroupEmployees.length}
        message={message}
      />
    </Box>
  );
};

export default TextMessage;